import { useEffect, useRef, useState } from 'react'
import { motion, useMotionValue, useTransform, useReducedMotion, animate } from 'motion/react'
import { Text } from '@astryxdesign/core/Text'
import { Logo } from '#/components/Logo'
import { drain, pullChanges } from '#/lib/sync'

/*
  Pull down to sync.

  "Reload the page" is the wrong thing to do to this app: every screen reads
  from IndexedDB, so a reload would throw away the shell and come back to the
  same rows. What the gesture should mean is "go and check" — drain whatever is
  queued, then pull anything the others have added. That is what it does.

  The mark is the spinner. It draws in as you pull — scaling and fading up from
  nothing — and turns while the sync runs, which gives the wait a shape without
  needing a second piece of art.

  This owns the scroll container so it can tell "at the top and pulling down"
  from an ordinary scroll, and so touchmove can be registered non-passively:
  without that the browser runs its own overscroll at the same time and the two
  fight each other.
*/

const TRIGGER = 72
/** Where it parks while the sync runs. */
const REST = 56
/** Past halfway the pull gets heavy, so it cannot be dragged to the floor. */
const resist = (d: number) => (d < TRIGGER ? d : TRIGGER + (d - TRIGGER) * 0.35)

export function PullToRefresh({ children }: { children: React.ReactNode }) {
  const viewport = useRef<HTMLDivElement>(null)
  const y = useMotionValue(0)
  const [busy, setBusy] = useState(false)
  const reduced = useReducedMotion()

  // The mark arrives with the pull rather than appearing at a threshold, so
  // the gesture feels connected to something from the first pixel.
  const markOpacity = useTransform(y, [0, TRIGGER * 0.5, TRIGGER], [0, 0.55, 1])
  const markScale = useTransform(y, [0, TRIGGER], [0.6, 1])

  useEffect(() => {
    const el = viewport.current
    if (!el) return

    let startY = 0
    let pulling = false

    const onStart = (e: TouchEvent) => {
      // Only from a genuine resting top. Mid-scroll the gesture belongs to the
      // scroller, and a multi-touch is a pinch, not a pull.
      if (busy || el.scrollTop > 0 || e.touches.length !== 1) return
      startY = e.touches[0].clientY
      pulling = true
    }

    const onMove = (e: TouchEvent) => {
      if (!pulling) return
      const delta = e.touches[0].clientY - startY
      if (delta <= 0) {
        // Turned into a scroll up. Hand it back rather than half-owning it.
        pulling = false
        void animate(y, 0, { duration: 0.12 })
        return
      }
      // Non-passive, so this can actually win against the browser's own
      // overscroll. The listener below is registered with passive: false.
      e.preventDefault()
      y.set(resist(delta))
    }

    const onEnd = async () => {
      if (!pulling) return
      pulling = false
      const pulled = y.get()
      const spring = reduced
        ? { duration: 0 }
        : { type: 'spring' as const, stiffness: 420, damping: 38 }

      if (pulled < TRIGGER) { void animate(y, 0, spring); return }

      setBusy(true)
      void animate(y, REST, spring)
      try {
        // Send ours first: pulling someone else's copy of a row we have not
        // pushed yet is how an edit gets quietly overwritten.
        await drain()
        await pullChanges()
      } catch {
        // The sync layer owns the error surface — the badge in the header
        // already says "Sync failed". Swallowing it here keeps the gesture
        // from growing an error state of its own.
      }
      // Long enough to be read as an answer rather than a flicker.
      await new Promise((r) => setTimeout(r, 350))
      setBusy(false)
      void animate(y, 0, spring)
    }

    el.addEventListener('touchstart', onStart, { passive: true })
    el.addEventListener('touchmove', onMove, { passive: false })
    el.addEventListener('touchend', onEnd, { passive: true })
    el.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      el.removeEventListener('touchstart', onStart)
      el.removeEventListener('touchmove', onMove)
      el.removeEventListener('touchend', onEnd)
      el.removeEventListener('touchcancel', onEnd)
    }
  }, [busy, reduced, y])

  return (
    <div ref={viewport} className="ptr-viewport">
      <motion.div className="ptr-track" style={{ y }}>
        <motion.div
          className="ptr-indicator"
          style={{ opacity: markOpacity, scale: markScale }}
          aria-hidden
        >
          <motion.div
            animate={busy && !reduced ? { rotate: 360 } : { rotate: 0 }}
            transition={busy && !reduced
              ? { repeat: Infinity, ease: 'linear', duration: 1.1 }
              : { duration: 0.2 }}
          >
            <Logo size={30} />
          </motion.div>
          <Text type="supporting">{busy ? 'Syncing…' : 'Pull to sync'}</Text>
        </motion.div>

        {/* Announced rather than drawn: the spinner is decorative, but a
            screen-reader user still needs to know a sync started. */}
        <div role="status" aria-live="polite" className="visually-hidden">
          {busy ? 'Syncing' : ''}
        </div>

        {children}
      </motion.div>
    </div>
  )
}
